import { Account, AccountExternalPagesVisibility, ClientAccount } from '@lune-climate/lune'
import { stableJson } from '@lune-fe/lune-components-lib'
import {
    ButtonNew,
    CurrencyPickerNew,
    LabeledFormSectionNew,
    LuneThemeNew,
    Text,
    Tooltip,
} from '@lune-fe/lune-ui-lib'
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined'
import { Box, Link } from '@mui/material'
import Stack from '@mui/material/Stack'
import {
    Dispatch,
    FC,
    memo,
    ReactElement,
    ReactNode,
    SetStateAction,
    useCallback,
    useRef,
    useState,
} from 'react'
import { Field, Form } from 'react-final-form'

import {
    AccountFormKeys,
    AccountFormType,
    IAccountFormData,
} from 'components/AccountForm/AccountFormWrapper'
import AccountLogoUpload from 'components/AccountForm/AccountLogoUpload'
import { FormInputNew } from 'components/FormInputNew'
import { FormSelectNew } from 'components/FormSelectNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import { PublicPagesModal } from 'views/Settings/ClientAccounts'

const required = (value: string | undefined) =>
    value?.trim() ? undefined : 'This is a required field'

const NotAllowedToEditTestAccountNameTooltip = ({
    children,
    contentWidth,
}: {
    children: ReactElement
    contentWidth?: boolean
}) => (
    <Tooltip
        title="Updating a test account's name is not allowed."
        placement="top"
        contentWidth={contentWidth}
    >
        {children}
    </Tooltip>
)

const FormSection: FC<{
    title: string
    subtitle?: string
    children: ReactNode
}> = ({ title, subtitle, children }) => {
    return (
        <WhiteBoxContainer
            sx={{
                p: 3,
            }}
        >
            <Text variant={'h6'}>{title}</Text>
            {subtitle && (
                <Text variant={'body3'} sx={{ color: `TextSecondary`, mt: 1, display: 'block' }}>
                    {subtitle}
                </Text>
            )}
            <Box
                sx={{
                    mt: 3,
                    maxWidth: '422px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                }}
            >
                {children}
            </Box>
        </WhiteBoxContainer>
    )
}

const { palette } = LuneThemeNew

const AccountForm = ({
    onSubmitForm,
    initialValues,
    readOnly,
    disabledFields,
    existingAccount,
    setFormValue,
    forbiddenTooltip,
    formType,
    account,
    onLogoUpdate,
    logoUpdating,
}: {
    onSubmitForm: (data: IAccountFormData) => Promise<any>
    disabledFields?: AccountFormKeys[]
    existingAccount?: boolean
    readOnly?: boolean
    initialValues: IAccountFormData
    setFormValue: Dispatch<SetStateAction<IAccountFormData | undefined>>
    forbiddenTooltip?: FC<{ children: ReactElement; contentWidth?: boolean }>
    formType: AccountFormType
    account?: ClientAccount | Account
    onLogoUpdate?: (file: File | null) => void
    logoUpdating?: boolean
}) => {
    const [openModal, setOpenModal] = useState<boolean>(false)
    const previousValuesRef = useRef<string>('')

    // To prevent rerendering when the other form elements change define the component here
    const CurrencyPickerComponent = useCallback(
        ({ input: { onChange, value }, meta }: any) => (
            <CurrencyPickerNew
                data-testid="account-currency-picker"
                error={meta.touched && meta.error}
                value={value}
                disabled={readOnly || existingAccount}
                onChange={onChange}
            />
        ),
        [readOnly, existingAccount],
    )
    return (
        <>
            <Form
                onSubmit={onSubmitForm}
                initialValues={initialValues}
                render={({ handleSubmit, values }) => {
                    // Only update formValue if values actually changed to prevent infinite loops
                    const valuesStr = JSON.stringify(values)
                    if (stableJson(valuesStr) !== stableJson(values)) {
                        previousValuesRef.current = valuesStr
                        setFormValue(values)
                    }
                    return (
                        <form style={{ position: `relative`, zIndex: 2 }} onSubmit={handleSubmit}>
                            <Stack
                                direction="column"
                                sx={{
                                    mb: -2,
                                    width: '100%',
                                }}
                                spacing={{
                                    xs: 3 / 2,
                                }}
                            >
                                <FormSection title={'Account details'}>
                                    <LabeledFormSectionNew title={'Name'}>
                                        <Field
                                            disabled={
                                                readOnly ||
                                                disabledFields?.includes(AccountFormKeys.NAME)
                                            }
                                            name={'name'}
                                            placeholder={'Name'}
                                            component={FormInputNew}
                                            validate={required}
                                            tooltip={
                                                disabledFields?.includes(AccountFormKeys.NAME)
                                                    ? NotAllowedToEditTestAccountNameTooltip
                                                    : forbiddenTooltip
                                            }
                                        />
                                    </LabeledFormSectionNew>
                                    {account && onLogoUpdate && (
                                        <AccountLogoUpload
                                            account={account}
                                            readOnly={readOnly}
                                            forbiddenTooltip={forbiddenTooltip}
                                            onLogoUpdate={onLogoUpdate}
                                            updating={logoUpdating}
                                        />
                                    )}
                                </FormSection>
                                <FormSection
                                    title={
                                        formType === AccountFormType.ACCOUNT
                                            ? 'Public pages'
                                            : 'Customer pages'
                                    }
                                    subtitle={
                                        formType === AccountFormType.ACCOUNT
                                            ? 'Public pages for sharing climate impact data with your customers, including offset purchases, emissions analytics, and reports'
                                            : 'Customer-branded pages for sharing climate impact data with your customers, including offset purchases, emissions analytics, and reports'
                                    }
                                >
                                    <LabeledFormSectionNew
                                        title={'Handle'}
                                        subTile={
                                            'URL-friendly unique identifier representing your customer'
                                        }
                                    >
                                        <Field
                                            disabled={readOnly}
                                            name={AccountFormKeys.HANDLE}
                                            placeholder={'Handle'}
                                            component={FormInputNew}
                                            tooltip={forbiddenTooltip}
                                        />
                                    </LabeledFormSectionNew>
                                    <LabeledFormSectionNew
                                        title={'Customer page visibility'}
                                        subTile={
                                            values.externalPagesVisibility ===
                                                AccountExternalPagesVisibility.HIDDEN ||
                                            !values.externalPagesVisibility
                                                ? 'Not accessible. Set to Public or Private to allow access'
                                                : values.externalPagesVisibility ===
                                                    AccountExternalPagesVisibility.PRIVATE
                                                  ? 'Visible via access controls'
                                                  : 'Visible to everyone with a link'
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Field
                                                disabled={readOnly}
                                                name={AccountFormKeys.EXTERNAL_PAGES_VISIBILITY}
                                                component={FormSelectNew}
                                                items={[
                                                    {
                                                        value: AccountExternalPagesVisibility.HIDDEN,
                                                        label: 'Hidden',
                                                    },
                                                    {
                                                        value: AccountExternalPagesVisibility.PUBLIC,
                                                        label: 'Public',
                                                    },
                                                    {
                                                        value: AccountExternalPagesVisibility.PRIVATE,
                                                        label: 'Private',
                                                    },
                                                ]}
                                                tooltip={forbiddenTooltip}
                                            />
                                            {account && (
                                                <ButtonNew
                                                    disabled={
                                                        values.externalPagesVisibility ===
                                                            AccountExternalPagesVisibility.HIDDEN ||
                                                        !values.externalPagesVisibility
                                                    }
                                                    onClick={() => setOpenModal(true)}
                                                >
                                                    Share
                                                </ButtonNew>
                                            )}
                                        </Box>
                                        <Link
                                            sx={{
                                                textDecorationColor: palette.TextPrimary,
                                                '&:hover, &:focus': {
                                                    textDecorationColor: palette.TextPrimary,
                                                },
                                                mt: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                            href={
                                                'https://docs.lune.co/general-concepts/accounts#client-accounts'
                                            }
                                            target={'_blank'}
                                        >
                                            <Text variant={'body3-medium'}>
                                                Learn about customer page visibility
                                            </Text>
                                            <LaunchOutlinedIcon
                                                sx={{
                                                    width: '20px',
                                                    height: '20px',
                                                    color: palette.TextPrimary,
                                                }}
                                            />
                                        </Link>
                                    </LabeledFormSectionNew>
                                </FormSection>
                                <FormSection
                                    title={'Offsetting'}
                                    subtitle={
                                        'These settings will apply to any purchase made for this account, unless overridden'
                                    }
                                >
                                    <LabeledFormSectionNew title={'Currency'}>
                                        <Field
                                            name={'currency'}
                                            validate={required}
                                            component={CurrencyPickerComponent}
                                            tooltip={forbiddenTooltip}
                                        />
                                    </LabeledFormSectionNew>
                                    <LabeledFormSectionNew
                                        title={'Beneficiary'}
                                        subTile={
                                            'The party receiving the environmental benefit from purchasing carbon credits. Overrides the Organisation’s beneficiary'
                                        }
                                    >
                                        <Field
                                            disabled={readOnly}
                                            name={'beneficiary'}
                                            placeholder={'Beneficiary'}
                                            component={FormInputNew}
                                            tooltip={forbiddenTooltip}
                                        />
                                    </LabeledFormSectionNew>
                                </FormSection>
                            </Stack>
                        </form>
                    )
                }}
            />
            {account && (
                <PublicPagesModal
                    account={account}
                    open={openModal}
                    onModalClose={() => setOpenModal(false)}
                />
            )}
        </>
    )
}

export default memo(AccountForm, (prev, next) => {
    return (
        stableJson(prev.initialValues) === stableJson(next.initialValues) &&
        stableJson(prev.account) === stableJson(next.account)
    )
})
